/** Margen para decidir si el esquema del sistema ha abierto algo. */
const ESPERA_RESERVA_MS = 1200;

const esAndroid = (): boolean => /Android/i.test(window.navigator.userAgent);

const esIos = (): boolean =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
  // iPadOS moderno se identifica como Mac con pantalla táctil
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

const abrirEnlace = (url: string): void => {
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.target = '_blank';
  enlace.rel = 'noopener noreferrer external';
  enlace.click();
};

/**
 * En un URI `geo:` la etiqueta va delimitada entre paréntesis, y muchos nombres
 * de campo los llevan dentro: "... DANI JARQUE (Artificial)". encodeURIComponent
 * NO escapa los paréntesis, así que hay que hacerlo a mano o el URI queda roto.
 */
const paraEtiquetaGeo = (texto: string): string =>
  encodeURIComponent(texto).replace(/\(/g, '%28').replace(/\)/g, '%29');

/**
 * Intenta abrir un esquema del sistema y, si no lo recoge nadie, abre una URL normal.
 *
 * No hay forma de saber si el esquema ha funcionado: el navegador no avisa. Lo
 * deducimos de que, si una aplicación se ha puesto delante, la página pierde el
 * foco o la visibilidad. Si al cabo de un momento seguimos aquí tal cual,
 * asumimos que no había ninguna app registrada.
 */
const abrirEsquemaConReserva = (esquema: string, reserva: string): void => {
  const inicio = Date.now();
  window.location.href = esquema;

  window.setTimeout(() => {
    const seguimosAqui = document.visibilityState === 'visible' && document.hasFocus();
    // Si el temporizador se ha retrasado mucho es que el navegador estuvo en
    // segundo plano, o sea que algo SÍ se abrió: no insistimos.
    const aTiempo = Date.now() - inicio < ESPERA_RESERVA_MS + 600;

    if (seguimosAqui && aTiempo) {
      abrirEnlace(reserva);
    }
  }, ESPERA_RESERVA_MS);
};

/**
 * Abre unas coordenadas en la aplicación de mapas del dispositivo.
 *
 * Android: usa el esquema `geo:`, que hace que el sistema muestre el selector de
 *          aplicaciones. Así el usuario abre con Google Maps, Waze o la que
 *          tenga, y puede cambiar de una vez para otra. Si nadie lo recoge
 *          -emulador de escritorio, móvil sin app de mapas- cae en Google Maps web.
 * iOS:     maps.apple.com abre la app de Mapas, la predeterminada del sistema.
 *          El esquema `geo:` no existe en iOS.
 * Resto:   Google Maps en el navegador.
 */
export const abrirEnMapas = (
  latitud?: string | null,
  longitud?: string | null,
  etiqueta?: string | null
): void => {
  if (!latitud || !longitud) return;

  const punto = `${latitud},${longitud}`;
  const nombre = etiqueta?.trim();
  const enNavegador = `https://www.google.com/maps/search/?api=1&query=${punto}`;

  if (esAndroid()) {
    const marcador = nombre ? `(${paraEtiquetaGeo(nombre)})` : '';
    abrirEsquemaConReserva(`geo:${punto}?q=${punto}${marcador}`, enNavegador);
    return;
  }

  if (esIos()) {
    const consulta = nombre ? `&q=${encodeURIComponent(nombre)}` : '';
    abrirEnlace(`https://maps.apple.com/?ll=${punto}${consulta}`);
    return;
  }

  abrirEnlace(enNavegador);
};

/** ¿Tenemos coordenadas suficientes para abrir el mapa? */
export const hayUbicacion = (latitud?: string | null, longitud?: string | null): boolean =>
  Boolean(latitud && longitud);
